import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Flex,
    Grid,
    Input,
    Spinner,
    Table,
    Text,
} from '@chakra-ui/react';
import {
    actualsFromHistory,
    calculateExitMultipleValuation,
    exitMultipleSensitivity,
    projectionsFromApiRows,
    statementAmounts,
} from './exitMultipleDcf';
import { formatCompact, formatSharePrice, parseAbbreviatedNumber } from './numberFormat';

function finite(value) {
    if (value == null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function formatOutflow(value) {
    const amount = finite(value);
    if (amount == null) return '—';
    if (amount < 0) return formatCompact(amount, { currency: true });
    return `(${formatCompact(amount, { currency: true })})`;
}

function formatFactor(value) {
    const amount = finite(value);
    return amount == null ? '—' : amount.toFixed(2);
}

function formatPeriod(value) {
    const amount = finite(value);
    return amount == null ? '—' : amount.toFixed(1);
}

function formatMultiple(value) {
    const amount = finite(value);
    return amount == null ? '—' : `${amount.toFixed(1)}x`;
}

function formatShareOfValue(value) {
    const amount = finite(value);
    if (amount == null) return '—';
    const percent = Math.round(amount * 1000) / 10;
    return Number.isInteger(percent) ? `${percent.toFixed(0)}%` : `${percent.toFixed(1)}%`;
}

function formatYearOverYear(current, previous) {
    const now = finite(current);
    const prior = finite(previous);
    if (now == null || prior == null || prior === 0) return null;
    const rounded = Math.round((((now - prior) / Math.abs(prior)) * 100) * 10) / 10;
    const body = `${Math.abs(rounded).toFixed(Number.isInteger(rounded) ? 0 : 1)}%`;
    if (rounded > 0) return `+${body}`;
    if (rounded < 0) return `-${body}`;
    return '0%';
}

function yearOverYearSeries(values) {
    return values.map((value, index) => formatYearOverYear(value, values[index - 1]));
}

function StackedFigure({ children, change }) {
    if (!change) return children;
    return (
        <Box>
            <Box>{children}</Box>
            <Text fontSize="xs" fontStyle="italic" fontWeight="normal" color="gray.500" lineHeight="shorter">
                {change}
            </Text>
        </Box>
    );
}

function ExhibitNumberInput({
    ariaLabel,
    value,
    onCommit,
    formatDisplay,
    formatDraft,
    parse = parseAbbreviatedNumber,
    width = '108px',
}) {
    const display = formatDisplay(value);
    const [draft, setDraft] = useState(display);
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!focused) setDraft(display);
    }, [display, focused]);

    const commit = (rawValue) => {
        const parsed = parse(rawValue);
        if (parsed == null || !Number.isFinite(parsed)) return;
        onCommit(parsed);
    };

    return (
        <Input
            aria-label={ariaLabel}
            value={focused ? draft : display}
            onChange={(event) => {
                setDraft(event.target.value);
                commit(event.target.value);
            }}
            onFocus={() => {
                setFocused(true);
                setDraft(formatDraft(value));
            }}
            onBlur={() => {
                setFocused(false);
                setDraft(display);
            }}
            size="xs"
            textAlign="right"
            w={width}
            fontVariantNumeric="tabular-nums"
            bg="white"
        />
    );
}

function currencyDraft(value) {
    return finite(value) == null ? '' : formatCompact(value, { currency: true });
}

function CalculatedAmount({ value, outflow = false, testId }) {
    return (
        <Text as="span" data-calculated="true" data-testid={testId} fontVariantNumeric="tabular-nums">
            {outflow ? formatOutflow(value) : formatCompact(value, { currency: true })}
        </Text>
    );
}

function summaryValue(value, { outflow = false } = {}) {
    return outflow ? formatOutflow(value) : formatCompact(value, { currency: true });
}

function ActualCell({ children, emphasize = false, boundary = false }) {
    return (
        <Table.Cell
            textAlign="right"
            color="gray.600"
            fontWeight={emphasize ? 'bold' : 'normal'}
            borderLeftWidth={boundary ? '2px' : undefined}
            borderLeftColor={boundary ? 'gray.800' : undefined}
            fontVariantNumeric="tabular-nums"
        >
            {children}
        </Table.Cell>
    );
}

function ForecastCell({ children, emphasize = false, boundary = false, shade = false }) {
    return (
        <Table.Cell
            textAlign="right"
            fontWeight={emphasize ? 'bold' : 'normal'}
            bg={shade ? 'gray.100' : undefined}
            borderLeftWidth={boundary ? '2px' : undefined}
            borderLeftColor={boundary ? 'gray.800' : undefined}
            fontVariantNumeric="tabular-nums"
        >
            {children}
        </Table.Cell>
    );
}

function PanelRow({ label, value, emphasize = false, rule = false, marker, children }) {
    return (
        <Flex
            justify="space-between"
            align="center"
            gap={3}
            py={1.5}
            borderTopWidth={rule ? '1px' : undefined}
            borderTopColor="gray.800"
            fontWeight={emphasize ? 'bold' : 'medium'}
        >
            <Text>
                {label}
                {marker && (
                    <Text as="span" color="gray.500" fontWeight="semibold" ml={2}>{marker}</Text>
                )}
            </Text>
            {children || (
                <Text data-calculated="true" fontVariantNumeric="tabular-nums">{value}</Text>
            )}
        </Flex>
    );
}

export default function DcfExhibit({
    history = [],
    apiRows,
    projections: savedProjections,
    projectionsCustomized = false,
    loading = false,
    error = null,
    taxRate,
    wacc,
    exitMultiple,
    totalDebt,
    preferredStock,
    noncontrollingInterest,
    cash,
    sharesOutstanding,
    currentPrice,
    dispatch,
}) {
    const apiProjections = useMemo(
        () => projectionsFromApiRows(apiRows, { taxRate }),
        [apiRows, taxRate],
    );
    const projections = savedProjections?.length ? savedProjections : apiProjections;
    const valuation = useMemo(() => calculateExitMultipleValuation({
        projections,
        wacc,
        exitMultiple,
        totalDebt,
        preferredStock,
        noncontrollingInterest,
        cash,
        sharesOutstanding,
    }), [
        projections,
        wacc,
        exitMultiple,
        totalDebt,
        preferredStock,
        noncontrollingInterest,
        cash,
        sharesOutstanding,
    ]);

    const actuals = useMemo(() => actualsFromHistory(history), [history]);
    const actualStatements = useMemo(
        () => actuals.map((row) => ({ ...row, ...statementAmounts(row) })),
        [actuals],
    );
    const lastActualYear = actuals.length ? actuals[actuals.length - 1].year : null;

    const editProjection = (year, field) => (value) => {
        dispatch({
            type: 'set_projection_field',
            year,
            field,
            value,
            fallbackRows: projections,
        });
    };

    const waccLabel = finite(wacc) == null ? 'WACC' : `${(wacc * 100).toFixed(1)}% WACC`;
    const price = finite(currentPrice);
    const upside = price != null && price > 0 && valuation.sharePrice != null
        ? valuation.sharePrice / price - 1
        : null;

    if (loading && projections.length === 0) {
        return <Flex justify="center" py={8}><Spinner color="green.500" /></Flex>;
    }
    if (error && projections.length === 0) {
        return <Text color="red.600">Unable to build the forecast: {error}</Text>;
    }
    if (projections.length === 0) {
        return <Text color="gray.600">Enter complete assumptions to build the forecast.</Text>;
    }

    const forecastHeaders = valuation.rows.map((row) => ({
        year: row.year,
        fiscalYear: lastActualYear == null ? null : lastActualYear + row.year,
    }));
    const revenueChange = yearOverYearSeries([
        ...actualStatements.map((row) => row.revenue),
        ...valuation.rows.map((row) => row.revenue),
    ]);
    const ebitdaChange = yearOverYearSeries([
        ...actualStatements.map((row) => row.ebitda),
        ...valuation.rows.map((row) => row.ebitda),
    ]);
    const ebitChange = yearOverYearSeries([
        ...actualStatements.map((row) => row.ebit),
        ...valuation.rows.map((row) => row.ebit),
    ]);
    const freeCashFlowChange = yearOverYearSeries([
        ...actualStatements.map((row) => row.unleveredFreeCashFlow),
        ...valuation.rows.map((row) => row.unleveredFreeCashFlow),
    ]);
    const changeAt = (series, index) => series[index];

    return (
        <Box>
            <Flex justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={3} mb={3} direction={{ base: 'column', md: 'row' }}>
                <Text color="gray.600" fontSize="sm">
                    Dollars, except per-share data. Forecast cash flows use a mid-year convention. Terminal value is final-year EBITDA times the exit multiple, discounted at year-end.
                </Text>
                {projectionsCustomized && (
                    <Button
                        size="sm"
                        variant="outline"
                        flexShrink={0}
                        onClick={() => dispatch({
                            type: 'reset_projections',
                            rows: apiProjections,
                        })}
                    >
                        Rebuild forecast
                    </Button>
                )}
            </Flex>
            {loading && <Text color="gray.600" fontSize="sm" mb={2}>Updating the seeded forecast…</Text>}
            <Box borderWidth="1px" borderColor="gray.300" overflow="hidden">
                <Box bg="gray.900" color="white" px={3} py={2}>
                    <Text fontWeight="bold">DCF Analysis</Text>
                </Box>
                <Box overflowX="auto">
                    <Table.Root
                        size="sm"
                        minW={`${Math.max(760, (forecastHeaders.length + actuals.length) * 132 + 240)}px`}
                        aria-label="DCF analysis"
                    >
                        <Table.Header>
                            <Table.Row bg="gray.200">
                                <Table.ColumnHeader
                                    position="sticky"
                                    left={0}
                                    zIndex={1}
                                    bg="gray.200"
                                    w={{ base: '8.75rem', md: 'auto' }}
                                    maxW={{ base: '8.75rem', md: 'none' }}
                                />
                                {actuals.map((column) => (
                                    <Table.ColumnHeader key={column.year} textAlign="right">
                                        <Text>{column.year}</Text>
                                    </Table.ColumnHeader>
                                ))}
                                {forecastHeaders.map((column) => (
                                    <Table.ColumnHeader
                                        key={column.year}
                                        textAlign="right"
                                        borderLeftWidth={column.year === forecastHeaders[0].year ? '2px' : undefined}
                                        borderLeftColor="gray.800"
                                        data-projection-boundary={column.year === forecastHeaders[0].year ? 'true' : undefined}
                                    >
                                        <Text>Year {column.year}</Text>
                                        {column.fiscalYear && (
                                            <Text fontSize="xs" fontWeight="medium" color="gray.700">{column.fiscalYear}E</Text>
                                        )}
                                    </Table.ColumnHeader>
                                ))}
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Table.Row>
                                <RowLabel>Revenue</RowLabel>
                                {actualStatements.map((column, index) => (
                                    <ActualCell key={column.year}>
                                        <StackedFigure change={changeAt(revenueChange, index)}>
                                            {formatCompact(column.revenue, { currency: true })}
                                        </StackedFigure>
                                    </ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} boundary={row.year === 1}>
                                        <StackedFigure change={changeAt(revenueChange, actualStatements.length + row.year - 1)}>
                                            <ExhibitNumberInput
                                                ariaLabel={`Year ${row.year} revenue`}
                                                value={row.revenue}
                                                formatDisplay={(amount) => formatCompact(amount, { currency: true })}
                                                formatDraft={currencyDraft}
                                                onCommit={editProjection(row.year, 'revenue')}
                                            />
                                        </StackedFigure>
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row>
                                <RowLabel>EBITDA</RowLabel>
                                {actualStatements.map((column, index) => (
                                    <ActualCell key={column.year}>
                                        <StackedFigure change={changeAt(ebitdaChange, index)}>
                                            {formatCompact(column.ebitda, { currency: true })}
                                        </StackedFigure>
                                    </ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} boundary={row.year === 1}>
                                        <StackedFigure change={changeAt(ebitdaChange, actualStatements.length + row.year - 1)}>
                                            <ExhibitNumberInput
                                                ariaLabel={`Year ${row.year} EBITDA`}
                                                value={row.ebitda}
                                                formatDisplay={(amount) => formatCompact(amount, { currency: true })}
                                                formatDraft={currencyDraft}
                                                onCommit={editProjection(row.year, 'ebitda')}
                                            />
                                        </StackedFigure>
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row>
                                <RowLabel>Less: Depreciation & Amortization</RowLabel>
                                {actualStatements.map((column) => (
                                    <ActualCell key={column.year}>{formatOutflow(column.depreciation)}</ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} boundary={row.year === 1}>
                                        <ExhibitNumberInput
                                            ariaLabel={`Year ${row.year} depreciation and amortization`}
                                            value={row.depreciation}
                                            formatDisplay={formatOutflow}
                                            formatDraft={currencyDraft}
                                            onCommit={editProjection(row.year, 'depreciation')}
                                        />
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row>
                                <RowLabel emphasize>EBIT</RowLabel>
                                {actualStatements.map((column, index) => (
                                    <ActualCell key={column.year} emphasize>
                                        <StackedFigure change={changeAt(ebitChange, index)}>
                                            <CalculatedAmount value={column.ebit} />
                                        </StackedFigure>
                                    </ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} emphasize boundary={row.year === 1}>
                                        <StackedFigure change={changeAt(ebitChange, actualStatements.length + row.year - 1)}>
                                            <CalculatedAmount value={row.ebit} />
                                        </StackedFigure>
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row>
                                <RowLabel>Less: Taxes</RowLabel>
                                {actualStatements.map((column) => (
                                    <ActualCell key={column.year}>{formatOutflow(column.taxes)}</ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} boundary={row.year === 1}>
                                        <ExhibitNumberInput
                                            ariaLabel={`Year ${row.year} taxes`}
                                            value={row.taxes}
                                            formatDisplay={formatOutflow}
                                            formatDraft={currencyDraft}
                                            onCommit={editProjection(row.year, 'taxes')}
                                        />
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row>
                                <RowLabel>Plus: Depreciation & Amortization</RowLabel>
                                {actualStatements.map((column) => (
                                    <ActualCell key={column.year}>
                                        <CalculatedAmount value={column.depreciationAddBack} />
                                    </ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} boundary={row.year === 1}>
                                        <CalculatedAmount value={row.depreciationAddBack} />
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row>
                                <RowLabel>Less: Capital Expenditures</RowLabel>
                                {actualStatements.map((column) => (
                                    <ActualCell key={column.year}>{formatOutflow(column.capex)}</ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} boundary={row.year === 1}>
                                        <ExhibitNumberInput
                                            ariaLabel={`Year ${row.year} capital expenditures`}
                                            value={row.capex}
                                            formatDisplay={formatOutflow}
                                            formatDraft={currencyDraft}
                                            onCommit={editProjection(row.year, 'capex')}
                                        />
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row>
                                <RowLabel>Less: Inc. / (Dec.) in Net Working Capital</RowLabel>
                                {actualStatements.map((column) => (
                                    <ActualCell key={column.year}>{formatOutflow(column.deltaNwc)}</ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} boundary={row.year === 1}>
                                        <ExhibitNumberInput
                                            ariaLabel={`Year ${row.year} change in net working capital`}
                                            value={row.deltaNwc}
                                            formatDisplay={formatOutflow}
                                            formatDraft={currencyDraft}
                                            onCommit={editProjection(row.year, 'deltaNwc')}
                                        />
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row>
                                <RowLabel emphasize>Unlevered FCF</RowLabel>
                                {actualStatements.map((column, index) => (
                                    <ActualCell key={column.year} emphasize>
                                        <StackedFigure change={changeAt(freeCashFlowChange, index)}>
                                            <CalculatedAmount value={column.unleveredFreeCashFlow} />
                                        </StackedFigure>
                                    </ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} emphasize boundary={row.year === 1}>
                                        <StackedFigure change={changeAt(freeCashFlowChange, actualStatements.length + row.year - 1)}>
                                            <CalculatedAmount value={row.unleveredFreeCashFlow} testId={row.year === 1 ? 'year-1-unlevered-fcf' : undefined} />
                                        </StackedFigure>
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row>
                                <RowLabel>Discount Period (mid-year convention)</RowLabel>
                                {actualStatements.map((column) => (
                                    <ActualCell key={column.year}>—</ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} boundary={row.year === 1}>
                                        <Text as="span" data-calculated="true">{formatPeriod(row.discountPeriod)}</Text>
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row>
                                <RowLabel>{`Discount Factor @ ${waccLabel}`}</RowLabel>
                                {actualStatements.map((column) => (
                                    <ActualCell key={column.year}>—</ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} boundary={row.year === 1}>
                                        <Text as="span" data-calculated="true">{formatFactor(row.discountFactor)}</Text>
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                            <Table.Row bg="gray.100">
                                <RowLabel emphasize shade>Present Value of FCF</RowLabel>
                                {actualStatements.map((column) => (
                                    <ActualCell key={column.year} emphasize>—</ActualCell>
                                ))}
                                {valuation.rows.map((row) => (
                                    <ForecastCell key={row.year} emphasize shade boundary={row.year === 1}>
                                        <CalculatedAmount value={row.presentValue} />
                                    </ForecastCell>
                                ))}
                            </Table.Row>
                        </Table.Body>
                    </Table.Root>
                </Box>
            </Box>

            <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={4} mt={4}>
                <Box borderWidth="1px" borderColor="gray.300">
                    <Box bg="gray.900" color="white" px={3} py={2}>
                        <Text fontWeight="bold" textAlign="center">Enterprise Value</Text>
                    </Box>
                    <Box px={3} py={2}>
                        <PanelRow
                            label="Cumulative Present Value of FCF"
                            value={summaryValue(valuation.cumulativePresentValue)}
                            marker="A"
                        />
                        <Text fontWeight="bold" mt={3}>Terminal Value</Text>
                        <PanelRow label="Terminal Year EBITDA" value={summaryValue(valuation.terminalYearEbitda)} />
                        <PanelRow label="Exit Multiple">
                            <ExhibitNumberInput
                                ariaLabel="Exit multiple"
                                value={exitMultiple}
                                width="88px"
                                formatDisplay={formatMultiple}
                                formatDraft={(amount) => (finite(amount) == null ? '' : String(amount))}
                                onCommit={(value) => dispatch({ type: 'set_field', field: 'exitMultiple', value })}
                            />
                        </PanelRow>
                        <PanelRow label="Terminal Value" value={summaryValue(valuation.terminalValue)} rule />
                        <PanelRow label="Discount Factor" value={formatFactor(valuation.terminalDiscountFactor)} />
                        <Box data-testid="terminal-value-source">
                            <PanelRow
                                label="Present Value of Terminal Value"
                                value={summaryValue(valuation.presentValueOfTerminalValue)}
                                emphasize
                                rule
                                marker="B"
                            />
                        </Box>
                        <PanelRow
                            label="% of Enterprise Value"
                            value={formatShareOfValue(valuation.terminalShareOfEnterpriseValue)}
                        />
                        <Flex bg="gray.200" mx={-3} mb={-2} mt={2} px={3} py={2} justify="space-between" align="center">
                            <Text fontWeight="bold">
                                Enterprise Value (A + B)
                                <Text as="span" color="gray.600" fontWeight="semibold" ml={2}>C</Text>
                            </Text>
                            <Text fontWeight="bold" data-calculated="true" data-testid="enterprise-value" fontVariantNumeric="tabular-nums">
                                {summaryValue(valuation.enterpriseValue)}
                            </Text>
                        </Flex>
                    </Box>
                </Box>

                <Box borderWidth="1px" borderColor="gray.300">
                    <Box bg="gray.900" color="white" px={3} py={2}>
                        <Text fontWeight="bold" textAlign="center">Implied Equity Value and Share Price</Text>
                    </Box>
                    <Box px={3} py={2}>
                        <PanelRow label="Enterprise Value" value={summaryValue(valuation.enterpriseValue)} />
                        <CapitalRow label="Less: Total Debt" ariaLabel="Total debt" value={totalDebt} dispatch={dispatch} field="totalDebt" />
                        <CapitalRow label="Less: Preferred Stock" ariaLabel="Preferred stock" value={preferredStock} dispatch={dispatch} field="preferredStock" />
                        <CapitalRow label="Less: Noncontrolling Interest" ariaLabel="Noncontrolling interest" value={noncontrollingInterest} dispatch={dispatch} field="noncontrollingInterest" />
                        <CapitalRow label="Plus: Cash and Cash Equivalents" ariaLabel="Cash and cash equivalents" value={cash} dispatch={dispatch} field="cash" outflow={false} />
                        <PanelRow label="Implied Equity Value" value={summaryValue(valuation.equityValue)} emphasize rule />
                        <Flex justify="space-between" align="center" gap={3} py={1.5}>
                            <Text fontWeight="medium">Fully Diluted Shares</Text>
                            <ExhibitNumberInput
                                ariaLabel="Fully diluted shares"
                                value={sharesOutstanding}
                                formatDisplay={(amount) => formatCompact(amount)}
                                formatDraft={(amount) => (finite(amount) == null ? '' : formatCompact(amount))}
                                onCommit={(value) => dispatch({ type: 'set_field', field: 'sharesOutstanding', value })}
                            />
                        </Flex>
                        <Flex bg="gray.200" mx={-3} mb={-2} mt={2} px={3} py={2} justify="space-between" align="center">
                            <Text fontWeight="bold">Implied Share Price</Text>
                            <Text fontWeight="bold" fontSize="xl" data-calculated="true" data-testid="implied-share-price" fontVariantNumeric="tabular-nums">
                                {formatSharePrice(valuation.sharePrice)}
                            </Text>
                        </Flex>
                    </Box>
                </Box>
            </Grid>
            {upside != null && (
                <Text mt={3} color="gray.700" fontSize="sm">
                    {formatCompact(Math.abs(upside), { percent: true })} {upside >= 0 ? 'above' : 'below'} the current price of {formatSharePrice(price)}.
                </Text>
            )}
            <Text mt={2} color="gray.600" fontSize="sm">
                Reported years stay fixed. Edit forecast-year revenue, EBITDA, depreciation, taxes, capex, or working capital. EBIT, unlevered free cash flow, present values, and the implied share price stay calculated.
            </Text>
        </Box>
    );
}

function RowLabel({ children, emphasize = false, shade = false }) {
    return (
        <Table.ColumnHeader
            scope="row"
            fontWeight={emphasize ? 'bold' : 'semibold'}
            whiteSpace={{ base: 'normal', md: 'nowrap' }}
            position="sticky"
            left={0}
            zIndex={1}
            w={{ base: '8.75rem', md: 'auto' }}
            maxW={{ base: '8.75rem', md: 'none' }}
            bg={shade ? 'gray.100' : 'white'}
        >
            {children}
        </Table.ColumnHeader>
    );
}

function CapitalRow({ label, ariaLabel, value, dispatch, field }) {
    return (
        <Flex justify="space-between" align="center" gap={3} py={1.5}>
            <Text fontWeight="medium">{label}</Text>
            <ExhibitNumberInput
                ariaLabel={ariaLabel}
                value={value}
                formatDisplay={(amount) => formatCompact(amount, { currency: true })}
                formatDraft={currencyDraft}
                onCommit={(next) => dispatch({ type: 'set_field', field, value: next })}
            />
        </Flex>
    );
}

export function ExitMultipleSensitivity({
    projections,
    wacc,
    exitMultiple,
    totalDebt,
    preferredStock,
    noncontrollingInterest,
    cash,
    sharesOutstanding,
}) {
    const cells = useMemo(() => exitMultipleSensitivity({
        projections,
        wacc,
        exitMultiple,
        totalDebt,
        preferredStock,
        noncontrollingInterest,
        cash,
        sharesOutstanding,
    }), [
        projections,
        wacc,
        exitMultiple,
        totalDebt,
        preferredStock,
        noncontrollingInterest,
        cash,
        sharesOutstanding,
    ]);
    if (cells.length === 0) return null;

    const waccs = [...new Set(cells.map((cell) => cell.wacc))];
    const multiples = [...new Set(cells.map((cell) => cell.exitMultiple))];

    return (
        <Box overflowX="auto">
            <Box
                as="table"
                borderCollapse="collapse"
                minW="560px"
                width="100%"
                aria-label="WACC by exit multiple sensitivity"
            >
                <Box as="thead">
                    <Box as="tr">
                        <Box as="th" p={2} textAlign="left">WACC ↓ / Exit multiple →</Box>
                        {multiples.map((multiple) => (
                            <Box as="th" key={multiple} p={2} textAlign="right">{formatMultiple(multiple)}</Box>
                        ))}
                    </Box>
                </Box>
                <Box as="tbody">
                    {waccs.map((rate) => (
                        <Box as="tr" key={rate}>
                            <Box as="th" p={2} textAlign="left">{(rate * 100).toFixed(1)}%</Box>
                            {multiples.map((multiple) => {
                                const cell = cells.find((candidate) => (
                                    candidate.wacc === rate && candidate.exitMultiple === multiple
                                ));
                                return (
                                    <Box
                                        as="td"
                                        key={multiple}
                                        p={2}
                                        textAlign="right"
                                        bg={cell?.isCurrent ? 'green.100' : undefined}
                                        borderWidth={cell?.isCurrent ? '2px' : '1px'}
                                        borderColor={cell?.isCurrent ? 'green.600' : 'gray.200'}
                                        aria-label={cell?.isCurrent ? 'Current assumptions' : undefined}
                                        data-calculated="true"
                                    >
                                        {formatSharePrice(cell?.sharePrice)}
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export function exhibitValuationInputs(state) {
    return {
        projections: state.projections,
        wacc: state.wacc,
        exitMultiple: state.exitMultiple,
        totalDebt: state.totalDebt,
        preferredStock: state.preferredStock,
        noncontrollingInterest: state.noncontrollingInterest,
        cash: state.cash,
        sharesOutstanding: state.sharesOutstanding,
    };
}
